<template>
    <v-app>
        <!-- MODAL -->
        <v-dialog v-model="dialog" max-width="500">
            <v-card>
                <v-card-title class="text-h6">
                    {{ form.id ? 'Edit Purpose' : 'Add Purpose' }}
                </v-card-title>

                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.name"
                                label="Purpose Name*"
                                density="compact"
                                variant="outlined"
                                :error-messages="errors.name"
                                required
                            />
                        </v-col>

                        <v-col cols="12">
                            <v-checkbox
                                v-model="form.status"
                                label="Active"
                                :true-value="1"
                                :false-value="0"
                                :error-messages="errors.status"
                                density="compact"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-divider></v-divider>
                <v-card-actions>
                    <v-layout class="justify-end">
                        <v-btn class="bg-red"  @click="dialog = false" >Close</v-btn>
                        <v-btn class="bg-brand w-15 ms-2" @click="savePurpose"> Save </v-btn>
                    </v-layout>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- PAGE -->
        <v-container fluid>
            <v-row>
                <v-col cols="12">
                    <v-expansion-panels v-model="panel">
                        <v-expansion-panel>
                            <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                                Purpose
                            </v-expansion-panel-title>

                            <v-expansion-panel-text>
                                <v-row class="py-2">
                                    <v-col md="4" cols="12">
                                        <v-text-field
                                            v-model="search"
                                            append-inner-icon="mdi-magnify"
                                            label="Search"
                                            single-line
                                            hide-details
                                            density="compact"
                                            variant="outlined"
                                            bg-color="grey-lighten-4"
                                            class="pl-2"
                                            clearable
                                        />
                                    </v-col>

                                    <v-col md="5" cols="12">
                                        <div class="d-flex align-center flex-column">
                                            <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                                                <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success"
                                                    @click="exportToCSV">CSV</v-btn>
                                                <v-btn prepend-icon="mdi mdi-download" variant="flat" color="primary"
                                                    @click="exportToExcel">EXCEL</v-btn>
                                                <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red"
                                                    @click="exportToPDF">PDF</v-btn>
                                            </v-btn-group>
                                        </div>
                                    </v-col>

                                    <v-col md="3" cols="12">
                                        <div class="text-right pr-2">
                                            <v-btn
                                                
                                                color="primary"
                                                variant="tonal"
                                                prepend-icon="mdi mdi-plus"
                                                rounded="xl"
                                                @click="openAdd"
                                            >
                                                Add Purpose
                                            </v-btn>
                                        </div>
                                    </v-col>
                                </v-row>

                                <v-data-table
                                    :headers="headers"
                                    :items="purposes"
                                    :loading="loading"
                                    :search="search"
                                    density="compact"
                                >
                                    <template #item.index="{ index }">
                                        {{ ++index }}
                                    </template>

                                    <template #item.status="{ item }">
                                        <v-chip v-if="item.status == 1" color="success" size="small">Active</v-chip>
                                        <v-chip v-else color="red" size="small">Inactive</v-chip>
                                    </template>

                                    <template #item.actions="{ item }">
                                        <v-icon
                                            color="primary"
                                            class="me-2"
                                            v-if="$updateAccess($route.fullPath)"
                                            @click="editItem(item)"
                                        >
                                            mdi mdi-pencil-outline
                                        </v-icon>
                                    </template>
                                </v-data-table>

                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-container>
    </v-app>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue'
    import { useStore } from 'vuex'
    import * as yup from 'yup'

    const store = useStore()

    /* STATE */
    const dialog = ref(false)
    const panel = ref([0])
    const loading = ref(false)
    const search = ref('')
    const errors = ref({})

    const form = ref({
        id: '',
        name: '',
        status: 1,
    })

    /* TABLE */
    const headers = [
        { title: "#SL", key: "index", align: "center", sortable: false },
        { title: "Code", key: "code", sortable: false },
        { title: "Name", key: "name", sortable: false },
        { title: "Status", key: "status" },
        { title: "Actions", key: "actions", align: "center", sortable: false },
    ]

    /* DATA */
    const purposes = computed(() => store.getters.getPurposeList)

    /* LIFECYCLE */
    onMounted(() => {
        loading.value = true
        store.dispatch('getPurposeList').finally(() => {
            loading.value = false
        })
    })

    /* METHODS */
    const openAdd = () => {
        form.value = { id: '', name: '', status: 1 }
        dialog.value = true
    }

    const editItem = (item) => {
        form.value = {
            id: item.id,
            name: item.name,
            status: item.status,
        }
        dialog.value = true
    }

    const schema = yup.object({
        name: yup
            .string()
            .required('Purpose name is required'),

        status: yup
            .number()
            .oneOf([0, 1], 'Invalid status')
            .required(),
    });

    const savePurpose = async () => {
        errors.value = {}

        try {
            await schema.validate(form.value, { abortEarly: false })

            const payload = {
                purpose_id: form.value.id,
                name: form.value.name,
                status: form.value.status,
            }

            await store.dispatch('saveUpdatePurpose', payload)

            dialog.value = false
            store.dispatch('getPurposeList')

        } catch (err) {
            if (err.inner) {
                err.inner.forEach(e => {
                    errors.value[e.path] = e.message
                })
            }
        }
    }

    /* EXPORT */
    const exportToPDF = () => {
        let sl = 1
        const columns = ['#SL', 'Code', 'Name', 'Status']
        const rows = purposes.value.map(i => [
            sl++, i.code, i.name, i.status ? 'Active' : 'Inactive'
        ])
        window.$exportTableToPDF(columns, rows, 'Purpose List', 'purpose_' + window.$fileDate())
    }

    const exportToExcel = () => {
        let data = [['#SL', 'Code', 'Name', 'Status']]
        let sl = 1
        purposes.value.forEach(i => {
            data.push([sl++, i.code, i.name, i.status ? 'Active' : 'Inactive'])
        })
        window.$exportTableToExcel(data, 'purpose_' + window.$fileDate())
    }

    const exportToCSV = () => {
        let data = [['#SL', 'Code', 'Name', 'Status']]
        let sl = 1
        purposes.value.forEach(i => {
            data.push([sl++, i.code, i.name, i.status ? 'Active' : 'Inactive'])
        })
        window.$exportTableToCSV(data, 'purpose_' + window.$fileDate())
    }
</script>
